/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { Callout, ExternalLink, Tooltip, LegacyDataTable, LegacyDataTableBody, LegacyDataTableRow, LegacyDataTableRowColumn, ActionButton } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { LicenseLimitStatus, LicenseMessage, LicenseResource, LicenseStatusResource, UpgradeConfigurationResource } from "@octopusdeploy/octopus-server-client";
import { LicenseMessageDisposition, Permission, UpgradeNotificationMode } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { usePageInstanceSummary } from "@octopusdeploy/portal-routes";
import { cloneDeep } from "lodash";
import { createSaveLicenseKeyAnalyticsEvent } from "~/areas/configuration/components/License/amplitudeAnalytics";
import { repository } from "~/clientInstance";
import { CodeEditor, TextFormat } from "~/components/CodeEditor/CodeEditor";
import ConfirmationDialog from "~/components/Dialog/ConfirmationDialog";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import { PageContent } from "~/components/PageContent/PageContent";
import { Section } from "~/components/Section/Section";
import { withTheme } from "~/components/Theme";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import type { StartWorkflow } from "~/workflows/useWorkflow";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
import styles from "./style.module.less";
interface LicensePageProps {
    trackEvent: ReturnType<typeof useTrackEvent>;
    page: ReturnType<typeof usePageInstanceSummary>;
    startUpdateNotificationsWorkflow: StartWorkflow<typeof workflows.updateNotificationsWorkflow>;
}
interface LicensePageState extends OptionalFormBaseComponentState<LicenseResource> {
    licenseStatus?: LicenseStatusResource;
    upgradeConfiguration?: UpgradeConfigurationResource;
    showConfirmation: boolean;
}
class LicensePageInternal extends OptionalFormBaseComponent<LicensePageProps, LicensePageState, LicenseResource> {
    constructor(props: LicensePageProps) {
        super(props);
        this.state = {
            showConfirmation: false,
        };
    }
    async componentDidMount() {
        return this.doBusyTask(async () => {
            const getLicence = repository.Licenses.getCurrent();
            const getLicenceStatus = repository.Licenses.getCurrentStatus();
            const getUpgradeConfiguration = repository.UpgradeConfiguration.get();
            const license = await getLicence;
            this.setState({
                upgradeConfiguration: await getUpgradeConfiguration,
                licenseStatus: await getLicenceStatus,
                model: license,
                cleanModel: cloneDeep(license),
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    async onSaveClick() {
        const isInitialLicenseRestricted = this.state.cleanModel?.LicenseText.includes("<Permissions>Restricted</Permissions>");
        const isCurrentLicenseRestricted = this.state.model?.LicenseText.includes("<Permissions>Restricted</Permissions>");
        if (!isInitialLicenseRestricted && isCurrentLicenseRestricted) {
            this.setState({ showConfirmation: true });
        }
        else {
            return this.save();
        }
    }
    save() {
        const start = performance.now();
        return this.doBusyTask(async () => {
            try {
                const licence = await repository.Licenses.getCurrent();
                licence.LicenseText = this.state.model!.LicenseText;
                const updated = await repository.Licenses.modifyCurrent(licence);
                const licenseStatus = await repository.Licenses.getCurrentStatus();
                this.setState({ model: updated, licenseStatus, cleanModel: cloneDeep(updated) });
                // Track successful save
                this.props.trackEvent(createSaveLicenseKeyAnalyticsEvent({
                    page: this.props.page,
                    durationInMs: performance.now() - start,
                    status: "Success",
                }));
            }
            catch (error) {
                // Track failed save
                this.props.trackEvent(createSaveLicenseKeyAnalyticsEvent({
                    page: this.props.page,
                    durationInMs: performance.now() - start,
                    status: "Failed",
                    error: error instanceof Error ? error : undefined,
                }));
                throw error;
            }
        });
    }
    render() {
        return withTheme((theme) => {
            const license = this.state.model;
            const licenseStatus = this.state.licenseStatus;
            const config = this.state.upgradeConfiguration;
            const isPtm = licenseStatus?.IsPtm ?? false;
            const renderAddLicenseKeyCallout = !this.state.cleanModel?.LicenseText;
            const renderNonComplianceCallout = licenseStatus ? !licenseStatus.IsCompliant : false;
            const renderExpiryWarningCallout = licenseStatus ? licenseStatus.DaysToEffectiveExpiryDate < 30 : false;
            const hasCallout = renderAddLicenseKeyCallout || renderNonComplianceCallout || renderExpiryWarningCallout;
            const sidebarLimits = !isPtm && licenseStatus && licenseStatus.Limits && licenseStatus.Limits.length > 0 && (<div key="licenseLimits">
                    <h4>License Limits</h4>
                    <LegacyDataTable>
                        <LegacyDataTableBody>
                            {licenseStatus.Limits.map((l: LicenseLimitStatus, index) => (<LegacyDataTableRow key={index}>
                                    <LegacyDataTableRowColumn>{l.Name}</LegacyDataTableRowColumn>
                                    <LegacyDataTableRowColumn>
                                        {l.CurrentUsage}/{l.LicenseLimitDescription}
                                    </LegacyDataTableRowColumn>
                                    <LegacyDataTableRowColumn>
                                        {l.Disposition === LicenseMessageDisposition.Information && (<Tooltip content={l.Message}>
                                                <em className={"fa-solid fa-circle-info"} style={{ color: theme.infoText }}/>
                                            </Tooltip>)}
                                        {l.Disposition === LicenseMessageDisposition.Notice && (<Tooltip content={l.Message}>
                                                <em className={"fa-solid fa-flag"} style={{ color: theme.infoText }}/>
                                            </Tooltip>)}
                                        {l.Disposition === LicenseMessageDisposition.Warning && (<Tooltip content={l.Message}>
                                                <em className={"fa-solid fa-triangle-exclamation"} style={{ color: theme.alertText }}/>
                                            </Tooltip>)}
                                        {l.Disposition === LicenseMessageDisposition.Error && (<Tooltip content={l.Message}>
                                                <em className={"fa-solid fa-exclamation-triangle"} style={{ color: theme.dangerText }}/>
                                            </Tooltip>)}
                                    </LegacyDataTableRowColumn>
                                </LegacyDataTableRow>))}
                        </LegacyDataTableBody>
                    </LegacyDataTable>
                </div>);
            const sidebarConfig = config && (<div key="upgradeNotifications">
                    <h4>Upgrade Notifications</h4>
                    <p>
                        The update notification that is shown in the top menu will <strong>{this.getUpgradeOption()}</strong>
                    </p>
                    <ActionButton label="Change" onClick={() => {
                    this.props.startUpdateNotificationsWorkflow((upgradeConfiguration) => this.setState({ upgradeConfiguration }));
                }}/>
                </div>);
            const body = licenseStatus && (<div key="licenseBody" className={hasCallout ? bodyStyles.calloutMargin : undefined}>
                    {renderAddLicenseKeyCallout && (<Section>
                            <Callout type="information" title="Add your license key to start using Octopus">
                                You can find your license key in your <ExternalLink href="ControlCenter">Control Center</ExternalLink> subscription.
                            </Callout>
                        </Section>)}
                    {renderNonComplianceCallout && (<Section>
                            <Callout type={"danger"} title="Your Octopus Server is not compliant with your license">
                                <p>{licenseStatus.ComplianceSummary}</p>
                                <p>
                                    Get more information or upgrade your license at <ExternalLink href="Purchase">octopus.com</ExternalLink>.
                                </p>
                            </Callout>
                        </Section>)}
                    {renderExpiryWarningCallout && (<Section>
                            <Callout type={licenseStatus.DaysToEffectiveExpiryDate > 0 ? "warning" : "danger"} title={this.getLicenceExpiryTitle(licenseStatus.DaysToEffectiveExpiryDate)}>
                                <p>Renewing your license guarantees access to:</p>

                                {licenseStatus.DoesExpiryBlockKeyActivities ? (<ul className={styles.list}>
                                        <li>Ongoing ability to create new releases and deployments</li>
                                        <li>All new features, enhancements, bug fixes, and patches</li>
                                        <li>Continued help and support</li>
                                    </ul>) : (<ul className={styles.list}>
                                        <li>All major features and enhancements</li>
                                        <li>Bug fixes and patches</li>
                                        <li>Continued help and support</li>
                                    </ul>)}
                                <p>
                                    Renewing your license is quick and easy. Visit our <ExternalLink href={`Renew?licenseKey=${license!.SerialNumber}`}>renewal and upgrade page</ExternalLink> for more information.
                                </p>
                            </Callout>
                        </Section>)}
                    <Section>
                        <div className={styles.licenceBox}>
                            <CodeEditor value={license!.LicenseText} allowFullScreen={false} language={TextFormat.XML} onChange={(v) => this.setState({ model: { ...license!, LicenseText: v } })} showToolbar={true} showCopyButton={true} placeholder="Paste license key here..."/>
                        </div>
                    </Section>
                    {licenseStatus.Messages && licenseStatus.Messages.length > 0 && (<Section>
                            <LegacyDataTable>
                                <LegacyDataTableBody>
                                    {licenseStatus.Messages.map((m: LicenseMessage, index) => (<LegacyDataTableRow key={index}>
                                            <LegacyDataTableRowColumn>
                                                {m.Disposition === LicenseMessageDisposition.Information && <em className={"fa-solid fa-circle-info"} style={{ color: theme.infoText }}/>}
                                                {m.Disposition === LicenseMessageDisposition.Warning && <em className={"fa-solid fa-triangle-exclamation"} style={{ color: theme.alertText }}/>}
                                                {m.Disposition === LicenseMessageDisposition.Error && <em className={"fa-solid fa-exclamation-triangle"} style={{ color: theme.dangerText }}/>}
                                            </LegacyDataTableRowColumn>
                                            <LegacyDataTableRowColumn>{m.Message}</LegacyDataTableRowColumn>
                                        </LegacyDataTableRow>))}
                                </LegacyDataTableBody>
                            </LegacyDataTable>
                        </Section>)}
                </div>);
            return (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} savePermission={{ permission: Permission.AdministerSystem }} onSaveClick={() => this.onSaveClick()} saveText="License applied">
                    {({ FormContent, createSaveAction }) => (<PageContent header={{ title: "License", primaryAction: createSaveAction({}) }} busy={this.state.busy} errors={this.errors} sidebar={<>
                                    {sidebarLimits}
                                    {sidebarConfig}
                                </>}>
                            <FormContent>
                                <ConfirmationDialog title="Switching to restricted permissions" continueButtonLabel="Confirm" open={this.state.showConfirmation} onClose={() => this.setState({ showConfirmation: false })} onContinueClick={async () => {
                        await this.save();
                        return true;
                    }}>
                                    <p>
                                        This license has <ExternalLink href="RestrictedPermissions">restricted permissions</ExternalLink>, which means that all users will be able to do everything in the system.
                                    </p>
                                    <p>Are you sure you want to continue?</p>
                                </ConfirmationDialog>
                                {body}
                            </FormContent>
                        </PageContent>)}
                </LegacyForm>);
        });
    }
    getUpgradeOption() {
        switch (this.state.upgradeConfiguration!.NotificationMode) {
            case UpgradeNotificationMode.AlwaysShow:
                return "show when any update is available";
            case UpgradeNotificationMode.ShowOnlyMajorMinor:
                return "show only when major or minor updates are available";
            default:
                return "never show";
        }
    }
    getLicenceExpiryTitle(expiresIn: number) {
        if (expiresIn < 0) {
            return `Your license expired ${-expiresIn} days ago`;
        }
        if (expiresIn === 0) {
            return "Your license expires today";
        }
        return `Your license expires in ${expiresIn} days`;
    }
}
export function LicensePage() {
    const trackEvent = useTrackEvent();
    const page = usePageInstanceSummary();
    const startUpdateNotificationsWorkflow = useWorkflow(workflows.updateNotificationsWorkflow);
    return <LicensePageInternal trackEvent={trackEvent} page={page} startUpdateNotificationsWorkflow={startUpdateNotificationsWorkflow}/>;
}
const bodyStyles = {
    calloutMargin: css({
        marginTop: space[8],
    }),
};
